/**
 * Decides whether `ChatPanelInner`'s document-level "ESC-to-close-
 * conversation" handler should ignore a given Escape keydown, rather than
 * deselecting the conversation. Two reasons to ignore it:
 *
 *   1. A Radix dialog (the image lightbox — see `ImageLightbox` — or any
 *      future one) is open and should consume this
 *      Escape itself. Detected via `[role="dialog"][data-state="open"]`,
 *      which reliably still matches on the *triggering* keydown: Radix's own
 *      `useEscapeKeydown` registers with `{capture: true}` so it always runs
 *      before this bubble-phase handler, but its resulting
 *      `onOpenChange(false)` is a React state update that flushes
 *      asynchronously — `data-state` on the dialog is still "open" in the DOM
 *      by the time this check runs on that same event.
 *
 *   2. `event.repeat` — an OS-level key-repeat continuation of a held
 *      Escape. This is the fix for a real bug: holding Escape a beat too
 *      long fires synthetic follow-up keydowns well after React's flush, by
 *      which point the dialog from (1) has already closed — an unguarded
 *      repeat event would then slip past the querySelector check and
 *      deselect the conversation, even though the user only meant to press
 *      Escape once. Ignoring repeats makes a held Escape a no-op after the
 *      first keydown; only a genuinely new keydown (key released and
 *      pressed again) can go on to close the conversation.
 *
 * Extracted to its own module (rather than inlined in `ChatPanel.tsx`'s
 * effect) so this exact decision can be unit-pinned without rendering the
 * whole panel, and so `ChatPanel.tsx` stays a component-only export (mixing
 * in a plain function trips `react-refresh/only-export-components`).
 */
export function shouldIgnoreConversationEscape(e: Pick<KeyboardEvent, 'repeat'>): boolean {
  if (e.repeat) return true;
  return document.querySelector('[role="dialog"][data-state="open"]') !== null;
}
